import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCloudUploadAlt,
  FaTimes,
  FaArrowLeft,
  FaFileAlt,
  FaCalendarAlt,
  FaTag,
  FaInfoCircle,
  FaLock,
  FaCheck,
} from "react-icons/fa";
import api from "../api/axios";
import "./DocumentManagement.css";
import { formatFileSize, getFileIcon, CATEGORIES, DOC_TYPES, ENTITY_TYPES } from "./docUtils";

export default function DocumentUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "General",
    document_type: "company",
    entity_type: "",
    entity_id: "",
    entity_name: "",
    has_expiry: false,
    expiry_date: "",
    expiry_alert_days: 30,
    tags: "",
    notes: "",
    is_private: false,
    uploaded_by: "",
  });

  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getEntityLabel = (entity, type) => {
    if (type === "employee") return `${entity.first_name || ""} ${entity.last_name || ""}`.trim();
    if (type === "customer") return entity.full_name || entity.name || entity.customer_name || "";
    if (type === "supplier") return entity.supplier_name || entity.name || "";
    return entity.name || entity.title || String(entity.id);
  };

  // Load entity list when entity_type changes
  const loadEntities = useCallback(async (type) => {
    if (!type || type === "") {
      setEntities([]);
      return;
    }
    setLoadingEntities(true);
    try {
      let res;
      if (type === "employee") res = await api.get("/employees/");
      else if (type === "customer") res = await api.get("/customers/");
      else if (type === "supplier") res = await api.get("/suppliers/");
      else {
        setEntities([]);
        setLoadingEntities(false);
        return;
      }
      const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data.employees || res.data.customers || res.data.suppliers || []);
      setEntities(data);
    } catch (e) {
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  }, []);

  useEffect(() => {
    if (form.entity_type && ["employee", "customer", "supplier"].includes(form.entity_type)) {
      loadEntities(form.entity_type);
    } else {
      setEntities([]);
    }
  }, [form.entity_type, loadEntities]);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!form.title) {
      setForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    if (name === "entity_id") {
      const selected = entities.find(en => String(en.id) === String(value));
      setForm(prev => ({
        ...prev,
        entity_id: value,
        entity_name: selected ? getEntityLabel(selected, form.entity_type) : "",
      }));
    } else if (name === "document_type") {
      setForm(prev => ({ ...prev, [name]: val, entity_type: "", entity_id: "", entity_name: "" }));
    } else {
      setForm(prev => ({ ...prev, [name]: val }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a file to upload."); return; }
    if (!form.title.trim()) { setError("Document title is required."); return; }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([key, val]) => {
        if (val !== null && val !== undefined && val !== "") {
          fd.append(key, val);
        }
      });

      await api.post("/document-management/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Document uploaded and indexed successfully.");
      setTimeout(() => navigate("/dashboard/documents/list"), 1000);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed. Please check your network and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showEntityPicker = form.document_type !== "company" && ["employee", "customer", "supplier"].includes(form.entity_type);

  return (
    <div className="doc-mgmt-container">
      <div className="doc-mgmt-header">
        <div className="doc-header-left">
          <div className="doc-header-icon-box">
            <FaCloudUploadAlt />
          </div>
          <div>
            <h1 className="doc-mgmt-title">Upload Document</h1>
            <p className="doc-mgmt-subtitle">
              Add corporate agreements, identity credentials, contracts, or financial statements
            </p>
          </div>
        </div>
        <button className="doc-btn-outline" onClick={() => navigate("/dashboard/documents/list")}>
          <FaArrowLeft />
          <span>Back to Repository</span>
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="row g-4">
          {/* Left Column: File Attachment & Expiry */}
          <div className="col-lg-5">
            {/* File Dropzone */}
            <div className="doc-detail-card" style={{ padding: 0 }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <span className="doc-section-title" style={{ margin: 0 }}>
                  <FaFileAlt /> Source File
                </span>
              </div>
              <div style={{ padding: "24px" }}>
                <div
                  className={`doc-upload-zone ${dragging ? "drag-over" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={e => handleFile(e.target.files[0])}
                  />
                  <div className="doc-upload-icon-wrap">
                    <FaCloudUploadAlt />
                  </div>
                  <div className="doc-upload-text">
                    {file ? "Choose different file" : "Drag and drop file here, or browse"}
                  </div>
                  <div className="doc-upload-sub">
                    Supports PDF, DOCX, XLSX, PNG, JPG, and CSV up to 50MB
                  </div>
                </div>

                {file && (
                  <div className="doc-file-preview">
                    {getFileIcon(file.type, file.name)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="preview-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file.name}
                      </div>
                      <div className="preview-size">
                        {formatFileSize(file.size)} • {file.type || "Document"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="doc-action-btn danger"
                      title="Remove selected file"
                      onClick={e => { e.stopPropagation(); setFile(null); }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Expiry Tracking */}
            <div className="doc-detail-card" style={{ padding: 0, marginTop: 20 }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <span className="doc-section-title" style={{ margin: 0 }}>
                  <FaCalendarAlt /> Expiry & Compliance Tracking
                </span>
              </div>
              <div style={{ padding: "24px" }}>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="has_expiry"
                    name="has_expiry"
                    checked={form.has_expiry}
                    onChange={handleChange}
                  />
                  <label className="form-check-label fw-600" htmlFor="has_expiry" style={{ fontSize: "13px" }}>
                    Enable expiration alerts for this document
                  </label>
                </div>
                {form.has_expiry && (
                  <>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                        Expiration Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="expiry_date"
                        value={form.expiry_date}
                        onChange={handleChange}
                        required={form.has_expiry}
                      />
                    </div>
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                          Alert Window
                        </label>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#0284c7" }}>
                          {form.expiry_alert_days} days prior
                        </span>
                      </div>
                      <input
                        type="range"
                        className="form-range"
                        min="7"
                        max="180"
                        step="1"
                        name="expiry_alert_days"
                        value={form.expiry_alert_days}
                        onChange={handleChange}
                      />
                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: "11px" }}>
                        <span>7 days</span>
                        <span>90 days</span>
                        <span>180 days</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tags & Security */}
            <div className="doc-detail-card" style={{ padding: 0, marginTop: 20 }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <span className="doc-section-title" style={{ margin: 0 }}>
                  <FaTag /> Indexing & Security
                </span>
              </div>
              <div style={{ padding: "24px" }}>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Metadata Tags
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="tags"
                    value={form.tags}
                    onChange={handleChange}
                    placeholder="e.g., license, legal, 2026, certified"
                  />
                  <div className="text-muted" style={{ fontSize: "11px", marginTop: 4 }}>
                    Separate multiple tags with commas
                  </div>
                </div>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="is_private"
                    name="is_private"
                    checked={form.is_private}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="is_private" style={{ fontSize: "13px" }}>
                    <FaLock style={{ fontSize: "11px", marginRight: 4, color: "#64748b" }} />
                    Restricted Access (Authorized Roles Only)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Metadata & Classification */}
          <div className="col-lg-7">
            <div className="doc-detail-card" style={{ padding: 0 }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <span className="doc-section-title" style={{ margin: 0 }}>
                  <FaInfoCircle /> Document Information
                </span>
              </div>
              <div style={{ padding: "24px" }}>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Document Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="e.g., Commercial Registration Certificate 2026"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Brief description or purpose of this file..."
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                      Category
                    </label>
                    <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                      Document Classification
                    </label>
                    <select className="form-select" name="document_type" value={form.document_type} onChange={handleChange}>
                      {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Entity Link */}
                {form.document_type !== "company" && (
                  <div className="mb-3" style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <label className="form-label" style={{ fontSize: "12px", fontWeight: 700, color: "#0f172a" }}>
                      Associate with Specific Entity
                    </label>
                    <div className="row g-2">
                      <div className="col-md-5">
                        <select
                          className="form-select"
                          name="entity_type"
                          value={form.entity_type}
                          onChange={handleChange}
                        >
                          <option value="">Choose entity type...</option>
                          {ENTITY_TYPES
                            .filter(et => ["employee", "customer", "supplier"].includes(et.value))
                            .map(et => (
                              <option key={et.value} value={et.value}>{et.label}</option>
                            ))
                          }
                        </select>
                      </div>
                      {showEntityPicker && (
                        <div className="col-md-7">
                          {loadingEntities ? (
                            <div className="form-control" style={{ color: "#94a3b8" }}>Loading records...</div>
                          ) : (
                            <select
                              className="form-select"
                              name="entity_id"
                              value={form.entity_id}
                              onChange={handleChange}
                            >
                              <option value="">Select party from CRM...</option>
                              {entities.map(en => (
                                <option key={en.id} value={en.id}>
                                  {getEntityLabel(en, form.entity_type)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Uploaded By (Recorder Name)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="uploaded_by"
                    value={form.uploaded_by}
                    onChange={handleChange}
                    placeholder="Staff or administrator name"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Internal Administrative Notes
                  </label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Internal reference notes, verification remarks, or auditing details..."
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="doc-btn-outline"
                onClick={() => navigate("/dashboard/documents/list")}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="doc-btn-primary"
                disabled={submitting || !file}
                style={{ minWidth: 150 }}
              >
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" />Processing...</>
                ) : (
                  <><FaCloudUploadAlt /><span>Upload & Index</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
